import React, { Suspense } from 'react';
import {Routes, Route, Navigate } from 'react-router-dom';
import NavBar from './components/NavBar';
import JobsMUI from './pages/JobsMUI';
import Dashboard from './pages/Dashboard';
import CompanyProfileManagement from './pages/CompanyProfileManagement';
import JobSeekerProfileManagement from './pages/JobSeekerProfileManagement';
import JobApply from './pages/JobApply';
import SimpleJobPost from './pages/SimpleJobPost';
import CompanyJobsManagement from './pages/CompanyJobsManagement';
import MyApplications from './pages/MyApplications';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import HomeRedirect from './components/HomeRedirect';
import { ColorModeProvider } from './hooks/useColorMode';
import JobBoardRevamp from './pages/JobBoardRevamp';
import TrainingLandingPage from './pages/TrainingLandingPage';
import TrainingConfirmationPage from './pages/TrainingConfirmationPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import VerifyPage from './pages/VerifyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import OnboardingProfileBuilder from './pages/OnboardingProfileBuilder';
import JobSeekerPublicPage from './pages/JobSeekerPublicPage';
import JobSeekerRoadmap from './pages/JobSeekerRoadmap';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SupportPage from './pages/SupportPage';
import SkilledNetworkApply from './pages/SkilledNetworkApply';
import McpLoginPage from './pages/McpLoginPage';
import GetJobReadyQuiz from './pages/GetJobReadyQuiz';
import InvoiceDashboard from './pages/invoice/InvoiceDashboard';
import InvoiceForm from './pages/invoice/InvoiceForm';
import InvoiceDetail from './pages/invoice/InvoiceDetail';
import OrganisationManager from './pages/invoice/OrganisationManager';
import IssuerProfile from './pages/invoice/IssuerProfile';
import useReferralTracking from './hooks/useReferralTracking';
import { useAuth } from './services/AuthService';

// Lazy-load admin components - only loaded when user is admin
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminCompanies = React.lazy(() => import('./pages/admin/AdminCompanies'));
const AdminJobSeekers = React.lazy(() => import('./pages/admin/AdminJobSeekers'));
const AdminJobSeekersMap = React.lazy(() => import('./pages/admin/AdminJobSeekersMap'));
const AdminJobs = React.lazy(() => import('./pages/admin/AdminJobs'));
const AdminApplications = React.lazy(() => import('./pages/admin/AdminApplications'));
const AdminSupport = React.lazy(() => import('./pages/admin/AdminSupport'));
const AdminCognito = React.lazy(() => import('./pages/admin/AdminCognito'));
const AdminBroadcastGroups = React.lazy(() => import('./pages/admin/AdminBroadcastGroups'));
const AdminWhatsappQRCodes = React.lazy(() => import('./pages/admin/AdminWhatsappQRCodes'));
const AdminWhatsappMessages = React.lazy(() => import('./pages/admin/AdminWhatsappMessages'));
const AdminNotifications = React.lazy(() => import('./pages/admin/AdminNotifications'));
const AdminInvoiceUsage = React.lazy(() => import('./pages/admin/AdminInvoiceUsage'));
const AdminFeedbacks = React.lazy(() => import('./pages/admin/AdminFeedbacks'));
const TrainingAdmin = React.lazy(() => import('./pages/admin/TrainingAdmin'));

// Loading component for admin routes
const AdminLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <div className="loading-spinner" style={{
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #1976d2',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      animation: 'spin 1s linear infinite'
    }} />
  </div>
);

function App() {
  useReferralTracking();
  const { isAdmin } = useAuth();

  return (
      <ColorModeProvider>
            <div className="App">
              <NavBar />
            <Routes>

              {/* Public Routes */}
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/jobs" element={<JobBoardRevamp />} />
              <Route path="/jobs-classic" element={<JobsMUI />} />
              <Route path="/jobs-new" element={<JobsMUI />} />
              <Route path="/training" element={<TrainingLandingPage />} />
              <Route path="/training/confirmation" element={<TrainingConfirmationPage />} />
              <Route path="/onboarding" element={<OnboardingProfileBuilder />} />
              <Route path="/jobseeker/public/:id" element={<JobSeekerPublicPage />} />

              {/* Auth Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/mcp-login" element={<McpLoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/get-job-ready" element={<GetJobReadyQuiz />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

              {/* Protected Routes */}
              <Route path="/company/post-job-simple" element={
                <ProtectedRoute>
                  <SimpleJobPost />
                </ProtectedRoute>
              } />
              <Route path="/company/jobs" element={
                <ProtectedRoute>
                  <CompanyJobsManagement />
                </ProtectedRoute>
              } />
              <Route path="/company/profile-management" element={
                <ProtectedRoute>
                  <CompanyProfileManagement />
                </ProtectedRoute>
              } />
              <Route path="/jobseeker/profile-management" element={
                <ProtectedRoute>
                  <JobSeekerProfileManagement />
                </ProtectedRoute>
              } />

              <Route path="/jobseeker/roadmap" element={
                <ProtectedRoute>
                  <JobSeekerRoadmap />
                </ProtectedRoute>
              } />
              <Route path="/jobseeker/my-applications" element={
                <ProtectedRoute>
                  <MyApplications />
                </ProtectedRoute>
              } />
              <Route path="/jobs/:id/apply" element={<JobApply />} />

              {/* Admin Routes - Only rendered when user is admin */}
              {isAdmin && (
                <>
                  <Route path="/admin" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminDashboard />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/companies" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminCompanies />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/job-seekers" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminJobSeekers />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/job-seekers-map" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminJobSeekersMap />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/jobs" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminJobs />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/applications" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminApplications />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/feedbacks" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminFeedbacks />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/support" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminSupport />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/cognito" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminCognito />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/broadcast-groups" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminBroadcastGroups />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/whatsapp-qrcodes" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminWhatsappQRCodes />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/whatsapp-messages" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminWhatsappMessages />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/notifications" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminNotifications />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/invoice-usage" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <AdminInvoiceUsage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/training" element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminLoading />}>
                        <TrainingAdmin />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                </>
              )}

              <Route path="/jobseeker/skilled-network" element={
                <ProtectedRoute>
                  <SkilledNetworkApply />
                </ProtectedRoute>
              } />
              <Route path="/support" element={
                <ProtectedRoute>
                  <SupportPage />
                </ProtectedRoute>
              } />

              {/* Invoice Routes */}
              <Route path="/invoice" element={
                <ProtectedRoute>
                  <InvoiceDashboard />
                </ProtectedRoute>
              } />
              <Route path="/invoice/new" element={
                <ProtectedRoute>
                  <InvoiceForm />
                </ProtectedRoute>
              } />
              <Route path="/invoice/:id" element={
                <ProtectedRoute>
                  <InvoiceDetail />
                </ProtectedRoute>
              } />
              <Route path="/invoice/:id/edit" element={
                <ProtectedRoute>
                  <InvoiceForm />
                </ProtectedRoute>
              } />
              <Route path="/invoice/organisations" element={
                <ProtectedRoute>
                  <OrganisationManager />
                </ProtectedRoute>
              } />
              <Route path="/invoice/profile" element={
                <ProtectedRoute>
                  <IssuerProfile />
                </ProtectedRoute>
              } />

              {/* Catch-all: unknown URLs (incl. admin paths for non-admins) go home */}
              <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
          </div>
      </ColorModeProvider>
  );
}

export default App;